import { useState } from 'react'
import { Search, X, Crown, RefreshCw, ShieldCheck, AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  useAdminUsers,
  useAdminUser,
  useGrantPremium,
  useRevokePremium,
  useReconcileUser,
  type AdminUserListItem,
  type PremiumSource,
} from '@/hooks/use-admin'

const sourceLabel: Record<PremiumSource, string> = {
  none: 'Free',
  comp: 'Comp',
  subscription: 'Subscription',
}

function SourceBadge({ source }: { source: PremiumSource }) {
  if (source === 'none') {
    return <span className="text-on-surface-variant text-xs">Free</span>
  }
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
        source === 'comp'
          ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
          : 'bg-primary/15 text-primary',
      )}
    >
      <Crown className="size-3" />
      {sourceLabel[source]}
    </span>
  )
}

function formatDate(iso: string | null) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export default function AdminPage() {
  const [search, setSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const { data, isLoading } = useAdminUsers(search)

  return (
    <div className="flex flex-1 flex-col px-6 pt-6 pb-8">
      <div className="mb-4 flex items-center gap-2">
        <ShieldCheck className="text-primary size-6" />
        <h1 className="font-heading text-2xl font-bold">Admin</h1>
      </div>
      <p className="text-on-surface-variant mb-4 text-sm">
        Manage users, premium grants, and billing. {data ? `${data.total} users.` : ''}
      </p>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="text-on-surface-variant absolute top-1/2 left-3 size-4 -translate-y-1/2" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by email or name…"
          className="border-outline-variant/40 bg-surface-container-low focus:border-primary w-full rounded-xl border py-2.5 pr-3 pl-9 text-sm outline-none"
        />
      </div>

      {/* User list */}
      {isLoading ? (
        <div className="text-on-surface-variant py-8 text-center text-sm">Loading…</div>
      ) : !data?.items.length ? (
        <div className="text-on-surface-variant py-8 text-center text-sm">No users found.</div>
      ) : (
        <div className="divide-outline-variant/20 bg-surface-container-low divide-y overflow-hidden rounded-xl">
          {data.items.map((u) => (
            <UserRow key={u.id} user={u} onClick={() => setSelectedId(u.id)} />
          ))}
        </div>
      )}

      {selectedId && <UserDrawer userId={selectedId} onClose={() => setSelectedId(null)} />}
    </div>
  )
}

function UserRow({ user, onClick }: { user: AdminUserListItem; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="hover:bg-surface-container-high flex w-full items-center justify-between gap-3 p-4 text-left transition-colors"
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <p className="truncate font-medium">{user.name}</p>
          {user.isAdmin && (
            <span className="bg-primary/15 text-primary rounded px-1.5 py-0.5 text-[10px] font-semibold">
              ADMIN
            </span>
          )}
        </div>
        <p className="text-on-surface-variant truncate text-sm">{user.email}</p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        <SourceBadge source={user.premiumSource} />
        <span className="text-on-surface-variant text-xs">{formatDate(user.createdAt)}</span>
      </div>
    </button>
  )
}

function UserDrawer({ userId, onClose }: { userId: string; onClose: () => void }) {
  const { data, isLoading } = useAdminUser(userId)
  const grant = useGrantPremium()
  const revoke = useRevokePremium()
  const reconcile = useReconcileUser()
  const [months, setMonths] = useState(12)

  const busy = grant.isPending || revoke.isPending || reconcile.isPending

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      {/* Panel */}
      <div className="bg-surface relative flex h-full w-full max-w-md flex-col overflow-y-auto shadow-xl">
        <div className="border-outline-variant/20 sticky top-0 flex items-center justify-between border-b bg-inherit p-4">
          <h2 className="font-semibold">User detail</h2>
          <button onClick={onClose} className="hover:bg-surface-container-high rounded-lg p-1">
            <X className="size-5" />
          </button>
        </div>

        {isLoading || !data ? (
          <div className="text-on-surface-variant p-6 text-sm">Loading…</div>
        ) : (
          <div className="flex flex-col gap-4 p-4">
            {/* Identity */}
            <section className="bg-surface-container-low rounded-xl p-4">
              <div className="flex items-center gap-2">
                <p className="font-semibold">{data.user.name}</p>
                {data.user.isAdmin && (
                  <span className="bg-primary/15 text-primary rounded px-1.5 py-0.5 text-[10px] font-semibold">
                    ADMIN
                  </span>
                )}
              </div>
              <p className="text-on-surface-variant text-sm">{data.user.email}</p>
              <div className="mt-2 flex items-center gap-2 text-sm">
                <SourceBadge source={data.user.premiumSource} />
                {data.user.premiumUntil && (
                  <span className="text-on-surface-variant">
                    until {formatDate(data.user.premiumUntil)}
                  </span>
                )}
              </div>
              <p className="text-on-surface-variant mt-2 text-xs">
                Joined {formatDate(data.user.createdAt)} · {data.user.locale}
              </p>
            </section>

            {/* Premium actions */}
            <section className="bg-surface-container-low rounded-xl p-4">
              <h3 className="mb-3 flex items-center gap-2 font-semibold">
                <Crown className="text-primary size-4" /> Premium
              </h3>
              <div className="mb-3 flex items-end gap-2">
                <label className="flex-1 text-sm">
                  <span className="text-on-surface-variant mb-1 block text-xs">Comp months</span>
                  <input
                    type="number"
                    min={1}
                    max={120}
                    value={months}
                    onChange={(e) => setMonths(Number(e.target.value))}
                    className="border-outline-variant/40 bg-surface w-full rounded-lg border px-3 py-2 text-sm outline-none"
                  />
                </label>
                <Button
                  className="rounded-lg"
                  disabled={busy || months < 1}
                  onClick={() => grant.mutate({ id: userId, body: { months } })}
                >
                  Grant / Extend
                </Button>
              </div>
              <Button
                variant="outline"
                className="text-destructive border-outline-variant/30 hover:bg-destructive/10 w-full rounded-lg"
                disabled={busy || data.user.premiumSource === 'none'}
                onClick={() => revoke.mutate({ id: userId })}
              >
                Revoke comp premium
              </Button>
              <p className="text-on-surface-variant mt-2 text-xs">
                Comp grants use <code>premium_until</code> and survive a Stripe reconcile. Revoke
                clears the comp; an active Stripe subscription keeps the user premium.
              </p>
            </section>

            {/* Subscriptions */}
            <section className="bg-surface-container-low rounded-xl p-4">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="font-semibold">Stripe subscriptions</h3>
                <Button
                  variant="outline"
                  className="border-outline-variant/30 h-8 rounded-lg px-2 text-xs"
                  disabled={busy}
                  onClick={() => reconcile.mutate({ id: userId })}
                >
                  <RefreshCw className={cn('mr-1 size-3', reconcile.isPending && 'animate-spin')} />
                  Reconcile
                </Button>
              </div>
              {data.subscriptions.length === 0 ? (
                <p className="text-on-surface-variant text-sm">No subscriptions.</p>
              ) : (
                <div className="space-y-2">
                  {data.subscriptions.map((s) => (
                    <div key={s.id} className="bg-surface rounded-lg p-3 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{s.status}</span>
                        {s.cancelAtPeriodEnd && (
                          <span className="text-amber-600 dark:text-amber-400">
                            cancels at period end
                          </span>
                        )}
                      </div>
                      <p className="text-on-surface-variant text-xs">
                        {s.stripePriceId ?? 'no price'} · renews {formatDate(s.currentPeriodEnd)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* LinkedIn usage */}
            <section className="bg-surface-container-low rounded-xl p-4">
              <h3 className="mb-2 font-semibold">LinkedIn imports</h3>
              <p className="text-sm">
                {data.linkedin.usedThisMonth} this month
                {data.linkedin.monthlyLimit !== null && ` / ${data.linkedin.monthlyLimit}`} ·{' '}
                {data.linkedin.totalImports} total
              </p>
              {data.linkedin.recentErrors.length > 0 && (
                <div className="mt-3 space-y-2">
                  <p className="text-on-surface-variant flex items-center gap-1 text-xs">
                    <AlertTriangle className="size-3" /> Recent failures
                  </p>
                  {data.linkedin.recentErrors.map((e) => (
                    <div key={e.id} className="bg-destructive/5 rounded-lg p-2 text-xs">
                      <p className="text-destructive truncate">{e.error ?? 'Unknown error'}</p>
                      <p className="text-on-surface-variant truncate">
                        {e.profileUrl ?? '—'} · {formatDate(e.importedAt)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  )
}
